import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import { Checkbox, FormControlLabel } from "@mui/material";
import config from "../../../config";
import getFeatureName from "../featureName";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN;

const DEFAULT_CENTER = [22.9527, 40.6253];
const DEFAULT_ZOOM = 11;
const EMPTY_FC = { type: "FeatureCollection", features: [] };

// Helper to update or add a source + layer
const updateOrAdd = (map, sourceId, layerId, layerConfig, data) => {
  const existingSource = map.getSource(sourceId);

  if (existingSource) {
    existingSource.setData(data);
  } else {
    map.addSource(sourceId, { type: "geojson", data });

    if (!map.getLayer(layerId)) {
      map.addLayer({ id: layerId, source: sourceId, ...layerConfig });
    }
  }
};

const APPROACH_LAYERS = ["approach-layer", "approach-highlight-layer"];
const EXIT_LAYERS = [
  "exit-line-layer",
  "exit-arrow-layer",
  "exit-highlight-line-layer",
  "exit-highlight-arrow-layer",
];

// Layers toggled together by each map checkbox
const layerGroup = (layerId) => {
  if (layerId === "exit-line-layer") {
    return [
      "exit-line-layer",
      "exit-arrow-layer",
      "exit-arrow-layer-outline",
      "exit-highlight-line-layer",
      "exit-highlight-arrow-layer",
      "exit-highlight-arrow-layer-outline",
    ];
  }
  if (layerId === "approach-layer") return APPROACH_LAYERS;
  return [layerId];
};

// Run fn now if the map's layers exist, otherwise once the map finishes its initial load.
// (isStyleLoaded() is false while any source is re-loading after setData, and "load" never
// fires again, so gating on it silently drops updates.)
const whenLayersReady = (map, fn) => {
  if (map.getSource("approach-source")) {
    fn();
  } else {
    map.once("load", fn);
  }
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildLabelHtml = (typeLabel, props, labels) => {
  const name = getFeatureName(props);
  let html = `<strong>${escapeHtml(typeLabel)}</strong><br/><strong>${escapeHtml(labels.id)}:</strong> ${escapeHtml(props?.id ?? "-")}`;
  if (name != null && name !== "") {
    html += `<br/><strong>${escapeHtml(labels.name)}:</strong> ${escapeHtml(name)}`;
  }
  return html;
};

// Hover labels for all geometry layers + click handling for approach features
const addHoverInteractivity = (map, onApproachClickRef, labelsRef) => {
  if (map.__hoverBound) return;
  map.__hoverBound = true;

  const hoverPopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });

  const bindHover = (layerId, typeKey) => {
    map.on("mouseenter", layerId, (e) => {
      map.getCanvas().style.cursor = "pointer";

      const feature = e.features && e.features[0];
      if (!feature) return;

      const labels = labelsRef.current;
      hoverPopup
        .setLngLat(e.lngLat)
        .setHTML(buildLabelHtml(labels[typeKey], feature.properties, labels))
        .addTo(map);
    });

    map.on("mousemove", layerId, (e) => {
      if (hoverPopup.isOpen()) hoverPopup.setLngLat(e.lngLat);
    });

    map.on("mouseleave", layerId, () => {
      map.getCanvas().style.cursor = "";
      hoverPopup.remove();
    });
  };

  APPROACH_LAYERS.forEach((layerId) => bindHover(layerId, "approach"));
  EXIT_LAYERS.forEach((layerId) => bindHover(layerId, "exit"));

  APPROACH_LAYERS.forEach((layerId) => {
    map.on("click", layerId, (e) => {
      const feature = e.features && e.features[0];
      if (!feature) return;

      onApproachClickRef.current?.(feature.properties?.id);
    });
  });
};

const filterFeaturesByIds = (featureCollection, ids) => {
  if (!ids || ids.length === 0) return EMPTY_FC;
  const idSet = new Set(ids);
  return {
    type: "FeatureCollection",
    features: (featureCollection?.features ?? []).filter((f) => idSet.has(f.properties?.id)),
  };
};

const collectCoordinates = (featureCollection, bounds) => {
  (featureCollection?.features ?? []).forEach((feature) => {
    const { geometry } = feature;
    if (!geometry) return;

    if (geometry.type === "LineString") {
      geometry.coordinates.forEach((coord) => bounds.extend(coord));
    } else if (geometry.type === "Polygon") {
      geometry.coordinates.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)));
    }
  });
};

function JunctionsMap({
  approachSource = EMPTY_FC,
  exitLineSource = EMPTY_FC,
  exitArrowSource = EMPTY_FC,
  selectedExitIds = [],
  selectedApproachId = null,
  onApproachClick,
}) {
  const { t } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const onApproachClickRef = useRef(onApproachClick);
  const labelsRef = useRef({});
  labelsRef.current = {
    approach: t("junctions.map.approach"),
    exit: t("junctions.map.exit"),
    id: t("junctions.map.id"),
    name: t("junctions.map.name"),
  };

  const [activeLayers, setActiveLayers] = useState({
    "approach-layer": true,
    "exit-line-layer": false,
  });

  const highlightLineSource = useMemo(
    () => filterFeaturesByIds(exitLineSource, selectedExitIds),
    [exitLineSource, selectedExitIds]
  );
  const highlightArrowSource = useMemo(
    () => filterFeaturesByIds(exitArrowSource, selectedExitIds),
    [exitArrowSource, selectedExitIds]
  );
  const highlightApproachSource = useMemo(
    () =>
      filterFeaturesByIds(approachSource, selectedApproachId == null ? [] : [selectedApproachId]),
    [approachSource, selectedApproachId]
  );

  useEffect(() => {
    onApproachClickRef.current = onApproachClick;
  }, [onApproachClick]);

  const renderLayers = (map) => {
    updateOrAdd(
      map,
      "approach-source",
      "approach-layer",
      {
        type: "line",
        slot: "top",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#2A3990", "line-width": 4, "line-emissive-strength": 1 },
      },
      approachSource
    );

    updateOrAdd(
      map,
      "approach-highlight-source",
      "approach-highlight-layer",
      {
        type: "line",
        slot: "top",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#FFD600", "line-width": 7, "line-emissive-strength": 1 },
      },
      highlightApproachSource
    );

    updateOrAdd(
      map,
      "exit-line-source",
      "exit-line-layer",
      {
        type: "line",
        slot: "top",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#C74811", "line-width": 4, "line-emissive-strength": 1 },
      },
      exitLineSource
    );

    updateOrAdd(
      map,
      "exit-arrow-source",
      "exit-arrow-layer",
      {
        type: "fill",
        slot: "top",
        paint: { "fill-color": "#C74811", "fill-opacity": 0.7, "fill-emissive-strength": 1 },
      },
      exitArrowSource
    );

    if (!map.getLayer("exit-arrow-layer-outline")) {
      map.addLayer({
        id: "exit-arrow-layer-outline",
        type: "line",
        source: "exit-arrow-source",
        slot: "top",
        paint: { "line-color": "#C74811", "line-width": 1, "line-emissive-strength": 1 },
      });
    }

    updateOrAdd(
      map,
      "exit-highlight-line-source",
      "exit-highlight-line-layer",
      {
        type: "line",
        slot: "top",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#2ECC71", "line-width": 6, "line-emissive-strength": 1 },
      },
      highlightLineSource
    );

    updateOrAdd(
      map,
      "exit-highlight-arrow-source",
      "exit-highlight-arrow-layer",
      {
        type: "fill",
        slot: "top",
        paint: { "fill-color": "#2ECC71", "fill-opacity": 0.85, "fill-emissive-strength": 1 },
      },
      highlightArrowSource
    );

    if (!map.getLayer("exit-highlight-arrow-layer-outline")) {
      map.addLayer({
        id: "exit-highlight-arrow-layer-outline",
        type: "line",
        source: "exit-highlight-arrow-source",
        slot: "top",
        paint: { "line-color": "#2ECC71", "line-width": 1.5, "line-emissive-strength": 1 },
      });
    }
  };

  // Initialize the map once
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/standard",
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      pitch: 45,
      bearing: 0,
      antialias: true,
      config: {
        basemap: {
          lightPreset: "night",
          showPointOfInterestLabels: false,
          showTransitLabels: false,
        },
      },
    });

    mapRef.current = map;
    map.once("load", () => {
      renderLayers(map);
      addHoverInteractivity(map, onApproachClickRef, labelsRef);
    });

    return () => map.remove();
  }, []);

  // Re-render layers and fit bounds whenever the geometry sources change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const doUpdate = () => {
      renderLayers(map);

      const bounds = new mapboxgl.LngLatBounds();
      collectCoordinates(approachSource, bounds);
      collectCoordinates(exitLineSource, bounds);
      collectCoordinates(exitArrowSource, bounds);

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 60, maxZoom: 17, duration: 800 });
      }

      Object.entries(activeLayers).forEach(([layerId, visible]) => {
        layerGroup(layerId).forEach((id) => {
          if (map.getLayer(id)) {
            map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
          }
        });
      });
    };

    whenLayersReady(map, doUpdate);
  }, [approachSource, exitLineSource, exitArrowSource]);

  // Keep the highlighted-exit layer in sync without refitting the map bounds
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const updateHighlight = () => {
      const lineSource = map.getSource("exit-highlight-line-source");
      const arrowSource = map.getSource("exit-highlight-arrow-source");
      const approachHighlight = map.getSource("approach-highlight-source");
      if (lineSource) lineSource.setData(highlightLineSource);
      if (arrowSource) arrowSource.setData(highlightArrowSource);
      if (approachHighlight) approachHighlight.setData(highlightApproachSource);
    };

    whenLayersReady(map, updateHighlight);
  }, [highlightLineSource, highlightArrowSource, highlightApproachSource]);

  const handleLayerToggle = (layerId) => {
    const map = mapRef.current;
    if (!map) return;

    const nextVisible = !activeLayers[layerId];

    layerGroup(layerId).forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", nextVisible ? "visible" : "none");
      }
    });

    setActiveLayers((prev) => ({ ...prev, [layerId]: nextVisible }));
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          background: "#202940",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={!!activeLayers["approach-layer"]}
              onChange={() => handleLayerToggle("approach-layer")}
            />
          }
          label={t("junctions.map.approaches")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!activeLayers["exit-line-layer"]}
              onChange={() => handleLayerToggle("exit-line-layer")}
            />
          }
          label={t("junctions.map.exits")}
        />
      </div>

      <div id="junctions-map" ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

export default JunctionsMap;
